import { IYourAppServer } from "../YourAppServer.service";
import Utils from "../../shared/utils";
import { StorageHive, LocalKeys } from "../valueStorage.service";
import { environment } from "../../../../environments/environment";
import { SocketURLs, SocketURLsModel, SocketTokenModel, SocketToken, SyncConnectionModel, SyncConnection, orderTypes, CheckoutHasPayment, CheckoutHasPaymentModel } from "../../shared/models";
import { YourAPPProductType } from "../../shared/contentTypes";
import { Subject, Observable } from "rxjs";

/** Status optionen */
export const enum ConnectionState {
  /** Verbindung wird momentan hergestellt */
  Connecting = 0,
  /** Verbindung besteht momentan */
  Connected = 1,
  /** Verbinug ist unterbrochen und wird versucht wieder hergestellt zu werden */
  Reconnecting = 2,
  /** Verbindung ist getrennt (Achtung bei JS ist es 4 nicht 3 wie in C#) */
  Disconnected = 4,
}

/** container für eine neue Connection */
export interface IConnectionData {
  readonly endpoint: string;
  readonly token: string;
  readonly key: string;
  readonly location: number;
}

/** container für eine neue Connection */
class ConnectionData implements IConnectionData {
  endpoint: string = "";
  token: string = "";
  key: string = "";
  location: number = NaN;
}

/** Connection to the YourAppServer Websocket */
export class YourAppServerSoket {

  public readonly Version: number = 1;

  /** enthält die information zur momentanen Verbindung */
  private _connection: ConnectionData = new ConnectionData();

  /** Cache für bekannte endpunkte, je Standort */
  private _cachedEndpoints: { [location: number]: string } = null;

  /** YourAppClientHubConnection */
  private _hubConnection: SignalR.Hub.Connection;
  /** YourAppClientHubProxy */
  private _yacProxy: SignalR.Hub.Proxy;
  /** Events, um zwischen einem erneuten Aufbau einer Verbindung, sie erneut automatisch zu binden. */
  private _events: [string, (...msg: any[]) => void][] = [];

  private _opend: Subject<void> = new Subject();
  /** Verbindung wurde geöffnet */
  public get ObserveOpend(): Observable<void> {
    return this._opend;
  }

  private _reconnecting: Subject<void> = new Subject();
  /** Wird aufgerufen wenn nach einem Fehler die Verbindung versucht wird wieder herzustellen.
   * Siehe auch https://learn.microsoft.com/de-de/aspnet/signalr/overview/guide-to-the-api/handling-connection-lifetime-events */
  public get ObserveReconnecting(): Observable<void> {
    return this._reconnecting;
  }

  private _reconnected: Subject<void> = new Subject();
  /** Wird aufgerufen wenn es erfolgreich wieder Verbunden wurde nach einem Timeout.
   *  Siehe auch https://learn.microsoft.com/de-de/aspnet/signalr/overview/guide-to-the-api/handling-connection-lifetime-events */
  public get ObserveReconnected(): Observable<void> {
    return this._reconnected;
  }

  private _closed: Subject<void> = new Subject();
  /** Verbindung wurde geschlossen */
  public get ObserveClosed(): Observable<void> {
    return this._closed;
  }

  private _unbind: Subject<void> = new Subject();
  /** Wird aufgerufen zur entgültigen Entbindung der per On<T...> gebundenen Anfragen */
  public get ObserveUnbind(): Observable<void> {
    return this._unbind;
  }

  /** Ermittelt den momentanen Connection Status */
  public get Status(): ConnectionState {
    return this._hubConnection ? this._hubConnection.state : ConnectionState.Disconnected;
  }

  /** Hub Connection Id, wenn verbunden, sonst null */
  public get HubId(): string {
    return this._hubConnection ? this._hubConnection.id : "";
  }

  /** Hub Connection Token, wenn verbunden */
  public get HubToken(): string {
    return this._hubConnection ? this._hubConnection.token : "";
  }

  /** Aktueller Endpunkt zu dem Verbunden wurde */
  public get Connection(): IConnectionData {
    return this._connection;
  }

  constructor(public readonly YourAppServer: IYourAppServer) {

  }

  /** Prüfung ob das Product eine SignalR verbindung unterstützt */
  private checkProductType(): void {
    if (environment.producttype !== YourAPPProductType.app_android &&
      environment.producttype !== YourAPPProductType.app_ios &&
      environment.producttype !== YourAPPProductType.webshop) {
      throw new Error("ProductType is not APP/Webshop and not support SignalR");
    }
  }

  private GetConnectionQueryString(): string {
    const me = this;
    // QueryString Key für Version
    const QS_Version = "v";
    // QueryString Key für HSAuth
    const QS_HSAuth = "a";
    // QueryString Key für HSKey
    const QS_HSKey = "k";
    const queryString = QS_Version + "=" + me.Version + "&" + QS_HSAuth + "=" + encodeURIComponent(me._connection.token) + "&" + QS_HSKey + "=" + encodeURIComponent(me._connection.key);
    return queryString;
  }

  /** Binding to SignalR, before Connecting */
  private BindTo(connection: ConnectionData, disconnectTimeout: number = 30, transportConnectTimeout: number = 0, reconnectDelay: number = 1000, reconnectWindow: number = 3000): void {
    const me = this;
    me.checkProductType();
    if (me._connection.location != connection.location || me._connection.endpoint != connection.endpoint) {
      if (!me._hubConnection) {
        me.Unbind();
      }
      me._connection = connection;

      const queryString = me.GetConnectionQueryString();
      me._hubConnection = $.hubConnection(me._connection.endpoint, { qs: queryString, useDefaultPath: true, logging: false });

      me._hubConnection.transportConnectTimeout = transportConnectTimeout;
      me._hubConnection.reconnectDelay = reconnectDelay;

      me._hubConnection.disconnected(() => {
        me._closed.next();
      })
      me._hubConnection.reconnecting(() => {
        // die werte werden hier nur gesetzt, weil sie aus negotiate vom Server überschrieben werden.
        // die zeiten werden hier gesetzt nur weil ich noch keine möglichkeit gefunden habe das Reconnecten zu unterbrechen weil die Authentifizierung abgelaufen ist
        // todo ein call der die credetials prüft mit queryString dann sofort disconnect, falls es 401 zurückkommt
        me._hubConnection.disconnectTimeout = disconnectTimeout;
        me._hubConnection.reconnectWindow = reconnectWindow;
        me._reconnecting.next();
      })
      me._hubConnection.reconnected(() => {
        me._reconnected.next();
      })
      me._hubConnection.connectionSlow(() => {
        me.YourAppServer.coreLogService.debug("YourAppServerSoket.BindTo", "_hubConnection.connectionSlow");
      })
      me._hubConnection.error((error) => {
        me.YourAppServer.coreLogService.error("YourAppServerSoket.BindTo", "_hubConnection.error", error);
      })
      me._hubConnection.received((data) => {
        me.YourAppServer.coreLogService.debug("YourAppServerSoket.BindTo", "_hubConnection.received", data);
      })

      me._yacProxy = me._hubConnection.createHubProxy("yac");

      // Wenn eine HubConnection erneuert wird, so werden hiermit die Events erneut gebunden
      for (let i = 0; i < me._events.length; ++i) {
        me._yacProxy.on(me._events[i][0], me._events[i][1]);
      }
    } else if (me._connection.token != connection.token) {
      // keep hubConnection only refresh queryString for new permissions
      me._connection = connection;
      me._hubConnection.qs = me.GetConnectionQueryString();
    }
  }

  /** entbindet sich von dem Hub */
  private Unbind(): void {
    const me = this;
    if (me._hubConnection) {
      // SignalR hat selbst kein Unbind, deswegen manuell per jQuery
      const $hubConnection = $(me._hubConnection);
      $hubConnection.unbind($.signalR.events.onDisconnect);
      $hubConnection.unbind("onReconnecting" /* "onReconnecting" = $.signalR.events.onReconnecting*/);
      $hubConnection.unbind($.signalR.events.onReconnect);
      $hubConnection.unbind($.signalR.events.onConnectionSlow);
      $hubConnection.unbind($.signalR.events.onError);
      $hubConnection.unbind($.signalR.events.onReceived);
      for (let i = 0; i < me._events.length; ++i) {
        me._yacProxy.off(me._events[i][0]);
      }

      // verweise lösen
      me._yacProxy = null;
      me._hubConnection = null;
      me._connection = new ConnectionData();
    }
  }

  /** Bindung an ein Event vom Server. Mit Off() kann sich wieder entbunden werden. */
  public On(eventName: string, callback: (...msg: any[]) => void): void {
    const me = this;
    me._events.push([eventName, callback]);
    if (me._yacProxy) {
      me._yacProxy.on(eventName, callback);
    }
  }

  /** Entbindung von einem Event vom Server.
   * @param callback ist optional, wenn nciht angegeben werden alle callbacks mit dem EventNamen entbunden
   */
  public Off(eventName: string, callback?: (...msg: any[]) => void): void {
    const me = this;
    for (let i = me._events.length - 1; i >= 0; --i) {
      if (me._events[i][0] == eventName && (!callback || me._events[i][1] == callback)) {
        me._events.splice(i, 1);
      }
    }
    if (me._yacProxy) {
      me._yacProxy.off(eventName, callback);
    }
  }

  /** Ruft eine Funktion auf dem Server auf. T ist die Antwort vom Server. */
  public Send<T>(eventName: string, ...args: any[]): Promise<T> {
    const me = this;
    return new Promise<T>((resolve, reject) => {
      if (me._yacProxy) {
        try {
          me._yacProxy.invoke(eventName, ...args).then((result) => {
            me.YourAppServer.coreLogService.debug("YourAppServerSoket.Send", "_yacProxy.invoke(" + eventName + ").then", result);
            resolve(result);
          }).catch((error: any) => {
            me.YourAppServer.coreLogService.error("YourAppServerSoket.Send", "_yacProxy.invoke(" + eventName + ").catch", error);
            reject(error);
          });
        } catch (error) {
          me.YourAppServer.coreLogService.error("YourAppServerSoket.Send", "_yacProxy.invoke(" + eventName + ") try catch", error);
          reject(error);
        }
      } else {
        me.YourAppServer.coreLogService.error("YourAppServerSoket.Send", "_yacProxy.invoke(" + eventName + ") _yacProxy is null ~ not connected");
        reject("not connected");
      }
    });
  }

  /** Beginnt die Verbindung zum Server oder ändert Sie, je Standort */
  public Connect(location: number): Promise<void> {
    const me = this;
    return new Promise<void>(async (resolve, reject) => {
      const newConnection = await me.GetTokenForLocation(location);
      if (!newConnection) {
        reject("no connection possible");
        return;
      }

      // bind if not bindet or rebind if other location or endpoint
      me.BindTo(newConnection);

      const options: SignalR.ConnectionOptions = {
        waitForPageLoad: false, // warten nicht notwendig
        transport: "auto", // oder einzeln ["webSockets", "serverSentEvents", "foreverFrame", "longPolling"]
      };
      me._hubConnection.start(options).done((data: any) => {
        me.YourAppServer.coreLogService.debug("YourAppServerSoket.Connect", "_hubConnection.start.done", data);
        me._opend.next();
        resolve();
      }).fail(() => {
        me.YourAppServer.coreLogService.error("YourAppServerSoket.Connect", "_hubConnection.start.fail");
        reject();
      }).catch((error: any) => {
        me.YourAppServer.coreLogService.error("YourAppServerSoket.Connect", "_hubConnection.start.catch", error);
        reject();
      });
    });
  }

  /** schreibt _currentData in den cache */
  private CacheData(location: number, endpoint: string): Promise<void> {
    const me = this;
    return new Promise<void>(async (resolve) => {
      try {
        if (!me._cachedEndpoints) {
          me._cachedEndpoints = {};
        }
        if (me._cachedEndpoints[location] !== endpoint) {
          me._cachedEndpoints[location] = endpoint;
          await me.YourAppServer.coreCOMService.environment.valueStorage.set(StorageHive.Local, LocalKeys.sockedUrl, me._cachedEndpoints);
        }
        resolve();
      } catch (error) {
        me.YourAppServer.coreLogService.error("YourAppServerSoket.CacheData", "catch", error, location, endpoint);
        resolve();
      }
    });
  }

  /** Ermittelt zuletzt gespeicherte, sonst null */
  private LoadCachedData(location: number): Promise<string> {
    const me = this;
    return new Promise<string>(async (resolve) => {
      try {
        if (me._cachedEndpoints) {
          me._cachedEndpoints = await me.YourAppServer.coreCOMService.environment.valueStorage.get(StorageHive.Local, LocalKeys.sockedUrl);
        }
        if (me._cachedEndpoints && me._cachedEndpoints[location]) {
          resolve(me._cachedEndpoints[location]);
        } else {
          resolve(""); // no endpoint found
        }
      } catch (error) {
        me.YourAppServer.coreLogService.error("YourAppServerSoket.LoadCachedData", "catch", error);
        resolve(""); // no endpoint found
      }
    });
  }

  /** Beginnt die Verbindung zum Server oder ändert Sie, je Standort */
  private GetTokenForLocation(location: number): Promise<ConnectionData> {
    const me = this;
    if (!location) {
      return Promise.resolve(null); // no connection possible
    }

    return new Promise<ConnectionData>(async (resolve) => {
      try {
        let token = "";
        const key = me.GenerateKey();
        // versuche mit gecacheten daten ein token zu ermitteln
        let endpoint = await me.LoadCachedData(location);
        if (endpoint.length > 0) {
          try {
            token = (await me.GetTokenFromServer(endpoint, location, key)).token;
          } catch (error) {
            me.YourAppServer.coreLogService.error("YourAppServerSoket.GetTokenForLocation", "GetTokenFromServer1.catch", error, endpoint, location);
          }
        }
        // versuche mit neuem endpoint ein token zu ermitteln
        if (Utils.IsNullOrEmpty(token)) {
          let newEndpoint = "";
          try {
            newEndpoint = (await me.GetURLsFromServer(location)).urls[0];
          } catch (error) {
            me.YourAppServer.coreLogService.error("YourAppServerSoket.GetTokenForLocation", "GetURLsFromServer.catch", error, location);
          }
          // nur wen endpoint sich auch vom vorherigen unterscheidet
          if (endpoint != newEndpoint && !Utils.IsNullOrEmpty(newEndpoint)) {
            try {
              token = (await me.GetTokenFromServer(newEndpoint, location, key)).token;
            } catch (error) {
              me.YourAppServer.coreLogService.error("YourAppServerSoket.GetTokenForLocation", "GetTokenFromServer2.catch", error, newEndpoint, location);
            }
            // bei erfolg, neuen endpoint cachen
            if (!Utils.IsNullOrEmpty(token)) {
              endpoint = newEndpoint;
              me.CacheData(location, endpoint); // no need to wait
            }
          }
        }

        if (!Utils.IsNullOrEmpty(token)) {
          const newConnection = new ConnectionData();
          newConnection.endpoint = endpoint;
          newConnection.token = token;
          newConnection.key = key;
          newConnection.location = location;
          resolve(newConnection);
        } else {
          resolve(null); // no connection possible
        }
      } catch (error) {
        me.YourAppServer.coreLogService.error("YourAppServerSoket.GetTokenForLocation", "catch", error, location);
        resolve(null); // no connection possible
      }
    });
  }

  /** Beendet die verbindung zum Server */
  public Disconnect(unbind: boolean = true): Promise<void> {
    const me = this;
    return new Promise<void>(async (resolve, reject) => {
      try {
        me._hubConnection.stop(false, true);
        if (unbind) {
          await me.Unbind();
        }
        resolve();
      } catch (error) {
        me.YourAppServer.coreLogService.error("YourAppServerSoket.Disconnect", "_hubConnection.stop.catch", error);
        reject();
      }
    });
  }

  /** Ruft den URLs ab, für eine sichere Socket Verbindung zum Server
   * @param location Standort
   * @param key Basis für den Token, muss bei der Verbindung zusammen mit dem Token angegeben werden
   */
  private GetURLsFromServer(location: number): Promise<SocketURLsModel> {
    const me = this;
    return new Promise<SocketURLsModel>((resolve, reject) => {
      try {
        const RequestData = new SocketURLs();
        RequestData.version = me.Version;
        RequestData.location = location;
        const FunctionName = "socket/GetURLs";
        const DataModelConstructor = SocketURLsModel;
        me.YourAppServer.Api.RequestService(DataModelConstructor, FunctionName, RequestData)
          .then((response) => {
            if (response.OK) {
              me.YourAppServer.coreLogService.debug("YourAppServerSoket.GetURLsFromServer", "RequestService (response OK)", response);
              // filter URLs there not "https://*.myhypersoftapp.de/*"
              response.Data.urls = response.Data.urls.filter((url) => {
                const urlParts = url.split(".");
                if (urlParts.length >= 3 && urlParts[0].startsWith("https://") && urlParts[1] == "myhypersoftapp" && (urlParts[2] == "de" || urlParts[2].startsWith("de/"))) {
                  return true;
                }
                me.YourAppServer.coreLogService.error("YourAppServerSoket.GetURLsFromServer", "RequestService got wrong url", url);
                return false;
              });
              resolve(response.Data);
            } else {
              me.YourAppServer.coreLogService.error("YourAppServerSoket.GetURLsFromServer", "RequestService (response not OK)", response);
              reject(response.HttpResponse.codeText);
            }
          })
          .catch((error) => { me.YourAppServer.coreLogService.error("YourAppServerSoket.GetURLsFromServer", "RequestService", error); reject(); });
      } catch (error) {
        me.YourAppServer.coreLogService.error("YourAppServerSoket.GetURLsFromServer", "try catch", error);
        reject();
      }
    });
  }

  /** Generiert ein Key, welcher bei GetTokenFromServer() verwendet werden kann. */
  private GenerateKey(): string {
    let key = "";
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    for (let i = 0; i < 5; ++i)
      key += charset[Math.floor(Math.random() * charset.length)];
    return key;
  }

  /** Ruft den Token ab, für eine sichere Socket Verbindung zum Server
  * @param url Hub Server URL
  * @param location Standort
  * @param key Basis für den Token, muss bei der Verbindung zusammen mit dem Token angegeben werden kann mit GenerateKey() generiert werden
  */
  private GetTokenFromServer(endpoint_url: string, location: number, key: string): Promise<SocketTokenModel> {
    const me = this;
    return new Promise<SocketTokenModel>((resolve, reject) => {
      try {
        const RequestData = new SocketToken();
        RequestData.version = me.Version;
        RequestData.location = location;
        RequestData.key = key;
        const FunctionName = "socket/GetToken";
        const DataModelConstructor = SocketTokenModel;
        // TODO Try get Token direkt from location Server endpoint_url
        me.YourAppServer.Api.RequestService(DataModelConstructor, FunctionName, RequestData)
          .then((response) => {
            if (response.OK) {
              me.YourAppServer.coreLogService.debug("YourAppServerSoket.GetTokenFromServer", "RequestService (response OK)", response);
              resolve(response.Data);
            } else {
              me.YourAppServer.coreLogService.error("YourAppServerSoket.GetTokenFromServer", "RequestService (response not OK)", response);
              reject(response.HttpResponse.codeText);
            }
          })
          .catch((error) => { me.YourAppServer.coreLogService.error("YourAppServerSoket.GetTokenFromServer", "RequestService", error); reject(); });
      } catch (error) {
        me.YourAppServer.coreLogService.error("YourAppServerSoket.GetTokenFromServer", "try catch", error);
        reject();
      }
    });
  }

  /** Synchronisiert SignalR mit Owin auf dem Server */
  public SyncConnectionWithServer(): Promise<boolean> {
    const me = this;
    return new Promise<boolean>(async (resolve) => {
      try {
        const result = await me._SyncConnectionWithServer()
        resolve(result.success);
      } catch (error) {
        me.YourAppServer.coreLogService.error("YourAppServerSoket.SyncConnectionWithServer", "try catch", error);
        resolve(false);
      }
    });
  }

  /** Synchronisiert SignalR mit Owin auf dem Server */
  private _SyncConnectionWithServer(): Promise<SyncConnectionModel> {
    const me = this;
    return new Promise<SyncConnectionModel>((resolve, reject) => {
      try {
        const RequestData = new SyncConnection();
        RequestData.token = me._connection.token;
        RequestData.key = me._connection.key;
        const FunctionName = "socket/SyncConnection";
        const DataModelConstructor = SyncConnectionModel;
        // TODO Try get Token direkt from location Server mit me._connection.endpoint als BasisURL und Achtung es wird der Beare Token fehlen und der Account kann nicht ermittelt werden 
        me.YourAppServer.Api.RequestService(DataModelConstructor, FunctionName, RequestData)
          .then((response) => {
            if (response.OK) {
              me.YourAppServer.coreLogService.debug("YourAppServerSoket._SyncConnectionWithServer", "RequestService (response OK)", response);
              resolve(response.Data);
            } else {
              me.YourAppServer.coreLogService.error("YourAppServerSoket._SyncConnectionWithServer", "RequestService (response not OK)", response);
              reject(response.HttpResponse.codeText);
            }
          })
          .catch((error) => { me.YourAppServer.coreLogService.error("YourAppServerSoket._SyncConnectionWithServer", "RequestService", error); reject(); });
      } catch (error) {
        me.YourAppServer.coreLogService.error("YourAppServerSoket._SyncConnectionWithServer", "try catch", error);
        reject();
      }
    });
  }

  /** Ermittelt vom Server ob InHouse eine Zahlungsart hat */
  public HasPaymentOnServer(location: number): Promise<boolean> {
    const me = this;
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        const result = await me._HasPaymentOnServer(location)
        resolve(result.has_payment);
      } catch (error) {
        me.YourAppServer.coreLogService.error("YourAppServerSoket.HasPaymentOnServer", "try catch", error);
        reject(error);
      }
    });
  }

  /** Ermittelt vom Server ob InHouse eine Zahlungsart hat */
  private _HasPaymentOnServer(location: number): Promise<CheckoutHasPaymentModel> {
    const me = this;
    return new Promise<CheckoutHasPaymentModel>((resolve, reject) => {
      try {
        const RequestData = new CheckoutHasPayment();
        RequestData.order_type = orderTypes.inHouse;
        RequestData.location = location;
        const FunctionName = "checkout/HasPayment";
        const DataModelConstructor = CheckoutHasPaymentModel;
        me.YourAppServer.Api.RequestService(DataModelConstructor, FunctionName, RequestData)
          .then((response) => {
            if (response.OK) {
              me.YourAppServer.coreLogService.debug("YourAppServerSoket._HasPaymentOnServer", "RequestService (response OK)", response);
              resolve(response.Data);
            } else {
              me.YourAppServer.coreLogService.error("YourAppServerSoket._HasPaymentOnServer", "RequestService (response not OK)", response);
              reject(response.HttpResponse.codeText);
            }
          })
          .catch((error) => { me.YourAppServer.coreLogService.error("YourAppServerSoket._HasPaymentOnServer", "RequestService", error); reject(); });
      } catch (error) {
        me.YourAppServer.coreLogService.error("YourAppServerSoket._HasPaymentOnServer", "try catch", error);
        reject();
      }
    });
  }
}
